// Analytics utility for MediGuide
// Supports multiple analytics providers

const ANALYTICS_ENABLED = process.env.REACT_APP_ANALYTICS_ENABLED === 'true';
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Event categories
export const EVENT_CATEGORIES = {
    USER: 'user',
    SYMPTOM_CHECK: 'symptom_check',
    NAVIGATION: 'navigation',
    SUBSCRIPTION: 'subscription',
    ENGAGEMENT: 'engagement',
    HEALTH_LIBRARY: 'health_library',
    FIND_CARE: 'find_care',
};

// Initialize analytics
export const initAnalytics = () => {
    if (!ANALYTICS_ENABLED) {
        console.log('Analytics disabled');
        return;
    }

    // Google Analytics 4
    if (GA_MEASUREMENT_ID) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        document.head.appendChild(script);

        window.dataLayer = window.dataLayer || [];
        function gtag() { window.dataLayer.push(arguments); }
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', GA_MEASUREMENT_ID, {
            page_path: window.location.pathname,
            anonymize_ip: true,
            cookie_flags: 'SameSite=None;Secure',
        });
    }

    console.log('Analytics initialized');
};

// Track page views
export const trackPageView = (path, title) => {
    if (!ANALYTICS_ENABLED) return;

    // Google Analytics
    if (window.gtag && GA_MEASUREMENT_ID) {
        window.gtag('config', GA_MEASUREMENT_ID, {
            page_path: path,
            page_title: title,
        });
    }

    // Console log for debugging
    console.log(`[Analytics] Page View: ${path}`);
};

// Track events
export const trackEvent = (category, action, label = null, value = null) => {
    if (!ANALYTICS_ENABLED) return;

    const eventData = {
        event_category: category,
        event_label: label,
        value: value,
    };

    // Google Analytics
    if (window.gtag) {
        window.gtag('event', action, eventData);
    }

    // Console log for debugging
    console.log(`[Analytics] Event: ${category}/${action}`, { label, value });
};

// Pre-defined tracking functions for common events

// User events
export const trackUserRegistration = (method = 'email') => {
    trackEvent(EVENT_CATEGORIES.USER, 'sign_up', method);
};

export const trackUserLogin = (method = 'email') => {
    trackEvent(EVENT_CATEGORIES.USER, 'login', method);
};

export const trackUserLogout = () => {
    trackEvent(EVENT_CATEGORIES.USER, 'logout');
};

export const trackProfileUpdate = (field) => {
    trackEvent(EVENT_CATEGORIES.USER, 'profile_update', field);
};

// Symptom check events
export const trackSymptomCheckStart = () => {
    trackEvent(EVENT_CATEGORIES.SYMPTOM_CHECK, 'start');
};

export const trackSymptomCheckStep = (step, bodyParts = []) => {
    trackEvent(EVENT_CATEGORIES.SYMPTOM_CHECK, `step_${step}`, bodyParts.join(','));
};

export const trackSymptomCheckComplete = (urgencyLevel) => {
    trackEvent(EVENT_CATEGORIES.SYMPTOM_CHECK, 'complete', urgencyLevel);
};

export const trackSymptomCheckAbandoned = (step) => {
    trackEvent(EVENT_CATEGORIES.SYMPTOM_CHECK, 'abandoned', `step_${step}`);
};

export const trackPhotoUpload = (success) => {
    trackEvent(EVENT_CATEGORIES.SYMPTOM_CHECK, 'photo_upload', success ? 'success' : 'failed');
};

// Navigation events
export const trackNavigation = (from, to) => {
    trackEvent(EVENT_CATEGORIES.NAVIGATION, 'navigate', `${from}_to_${to}`);
};

// Subscription events
export const trackSubscriptionView = () => {
    trackEvent(EVENT_CATEGORIES.SUBSCRIPTION, 'view_plans');
};

export const trackSubscriptionSelect = (plan) => {
    trackEvent(EVENT_CATEGORIES.SUBSCRIPTION, 'select_plan', plan);
};

export const trackPaymentStart = (method, plan) => {
    trackEvent(EVENT_CATEGORIES.SUBSCRIPTION, 'payment_start', `${method}_${plan}`);
};

export const trackPaymentComplete = (method, plan) => {
    trackEvent(EVENT_CATEGORIES.SUBSCRIPTION, 'payment_complete', `${method}_${plan}`);
};

export const trackPaymentFailed = (method, plan) => {
    trackEvent(EVENT_CATEGORIES.SUBSCRIPTION, 'payment_failed', `${method}_${plan}`);
};

// Engagement events
export const trackFeatureUse = (feature) => {
    trackEvent(EVENT_CATEGORIES.ENGAGEMENT, 'feature_use', feature);
};

export const trackShareDiagnosis = () => {
    trackEvent(EVENT_CATEGORIES.ENGAGEMENT, 'share_diagnosis');
};

export const trackDownloadPDF = () => {
    trackEvent(EVENT_CATEGORIES.ENGAGEMENT, 'download_pdf');
};

export const trackDarkModeToggle = (enabled) => {
    trackEvent(EVENT_CATEGORIES.ENGAGEMENT, 'dark_mode', enabled ? 'enabled' : 'disabled');
};

// Health Library events
export const trackHealthLibraryView = (category) => {
    trackEvent(EVENT_CATEGORIES.HEALTH_LIBRARY, 'view', category);
};

export const trackHealthLibrarySearch = (query) => {
    trackEvent(EVENT_CATEGORIES.HEALTH_LIBRARY, 'search', query);
};

export const trackLeafletView = (leafletId) => {
    trackEvent(EVENT_CATEGORIES.HEALTH_LIBRARY, 'leaflet_view', leafletId);
};

// Find Care events
export const trackFindCareView = () => {
    trackEvent(EVENT_CATEGORIES.FIND_CARE, 'view');
};

export const trackFindCareSearch = (facilityType) => {
    trackEvent(EVENT_CATEGORIES.FIND_CARE, 'search', facilityType);
};

export const trackFindCareDirections = (facilityType) => {
    trackEvent(EVENT_CATEGORIES.FIND_CARE, 'get_directions', facilityType);
};

// Timing events
export const trackTiming = (category, variable, time, label = null) => {
    if (!ANALYTICS_ENABLED) return;

    if (window.gtag) {
        window.gtag('event', 'timing_complete', {
            name: variable,
            value: time,
            event_category: category,
            event_label: label,
        });
    }
};

// Error tracking
export const trackError = (error, fatal = false) => {
    if (!ANALYTICS_ENABLED) return;

    if (window.gtag) {
        window.gtag('event', 'exception', {
            description: error.message || error,
            fatal: fatal,
        });
    }

    console.error(`[Analytics] Error tracked:`, error);
};

// User properties (for segmentation)
export const setUserProperties = (properties) => {
    if (!ANALYTICS_ENABLED) return;

    if (window.gtag) {
        window.gtag('set', 'user_properties', properties);
    }
};

// Set user ID (after login)
export const setUserId = (userId) => {
    if (!ANALYTICS_ENABLED) return;

    if (window.gtag && GA_MEASUREMENT_ID) {
        window.gtag('config', GA_MEASUREMENT_ID, {
            user_id: userId,
        });
    }
};

export default {
    initAnalytics,
    trackPageView,
    trackEvent,
    trackUserRegistration,
    trackUserLogin,
    trackUserLogout,
    trackSymptomCheckStart,
    trackSymptomCheckStep,
    trackSymptomCheckComplete,
    trackPaymentComplete,
    trackError,
};
